// ThemeSwitcher — compact dropdown for switching between Market Green,
// Warm Leather, and Ivory Blue. Lives in the TopNav. The Settings page
// has a richer card-based selector; this one is the one-click shortcut.

import { useState } from "react";
import { Palette, Check } from "lucide-react";
import { useTheme, VALID_THEMES, THEME_META, type Theme } from "@/contexts/ThemeContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Visual preview swatches for each theme so the user sees the palette
// at a glance in the dropdown. Daylight + Midnight are the redesigned
// pair from UI_REDESIGN_STANDARDS; the legacy three are preserved for
// backward compat (Clara Live forces warm-leather, etc.).
const THEME_SWATCHES: Record<Theme, { bg: string; fg: string; accent: string }> = {
  "daylight":     { bg: "#F4F1E9", fg: "#211E1A", accent: "#2D5A3D" },
  "midnight":     { bg: "#0A0E16", fg: "#F1ECDC", accent: "#5BB37D" },
  "market-green": { bg: "#0f1a0f", fg: "#e8e8e8", accent: "#c9a84c" },
  "warm-leather": { bg: "#070402", fg: "#F5E8D0", accent: "#D4A54A" },
  "ivory-blue":   { bg: "#F5F0E6", fg: "#221C17", accent: "#2E5D3E" },
};

export function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);

  const current = THEME_META[theme];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title={`Theme: ${current.label}`}
          className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <Palette className="h-3.5 w-3.5" />
          <span className="hidden xl:inline">{current.label}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
          Theme
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {VALID_THEMES.map((id) => {
          const meta = THEME_META[id];
          const swatch = THEME_SWATCHES[id];
          const isActive = theme === id;
          return (
            <DropdownMenuItem
              key={id}
              onClick={() => {
                setTheme(id);
                setOpen(false);
              }}
              className="flex items-start gap-3 py-2.5 cursor-pointer"
            >
              {/* Mini swatch preview */}
              <div
                className="flex-shrink-0 w-10 h-10 rounded border border-border overflow-hidden"
                style={{ backgroundColor: swatch.bg }}
              >
                <div
                  className="w-full h-1/2"
                  style={{ backgroundColor: swatch.fg, opacity: 0.2 }}
                />
                <div
                  className="w-full h-1/2"
                  style={{ backgroundColor: swatch.accent, opacity: 0.9 }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{meta.label}</span>
                  {isActive && <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />}
                </div>
                <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                  {meta.description}
                </div>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
